import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HTTPCONSTANTS } from '../http/http-constants';
import { EditorialArticle, EditorialStatus } from '../models/editorial/editorialArticle';
import {
  BatchResearchRequest,
  ResearchJob,
  SeedResearchRequest,
} from '../models/editorial/researchJob';

@Injectable({ providedIn: 'root' })
export class EditorialService {
  constructor(private http: HttpClient) {}

  private base(path: string): string {
    return `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}${path}`;
  }

  // ---- Public (plate detail page) -------------------------------------------

  // Returns the APPROVED editorial for a plate, or null (404 -> caller treats
  // as "no editorial yet"). Only approved articles are exposed by this route.
  public getApprovedEditorial(
    registration: string
  ): Observable<EditorialArticle> {
    return this.http.get<EditorialArticle>(
      this.base(`/editorial/${encodeURIComponent(registration)}`)
    );
  }

  // ---- Admin review ---------------------------------------------------------

  public listForReview(
    status: EditorialStatus | 'all' = EditorialStatus.DRAFT
  ): Observable<EditorialArticle[]> {
    return this.http.get<EditorialArticle[]>(
      this.base(`/admin/editorial?status=${status}`)
    );
  }

  public getArticle(id: number): Observable<EditorialArticle> {
    return this.http.get<EditorialArticle>(this.base(`/admin/editorial/${id}`));
  }

  // Persist admin edits to headline/summary/body/claims without changing status.
  public saveEdits(
    id: number,
    edits: Partial<EditorialArticle>
  ): Observable<EditorialArticle> {
    return this.http.put<EditorialArticle>(
      this.base(`/admin/editorial/${id}`),
      edits
    );
  }

  public approve(id: number, notes?: string): Observable<EditorialArticle> {
    return this.http.post<EditorialArticle>(
      this.base(`/admin/editorial/${id}/approve`),
      { notes }
    );
  }

  public reject(id: number, reason: string): Observable<EditorialArticle> {
    return this.http.post<EditorialArticle>(
      this.base(`/admin/editorial/${id}/reject`),
      { reason }
    );
  }

  public requestChanges(
    id: number,
    notes: string
  ): Observable<EditorialArticle> {
    return this.http.post<EditorialArticle>(
      this.base(`/admin/editorial/${id}/request-changes`),
      { notes }
    );
  }

  // Re-queue a fresh research pass for this plate (e.g. evidence was weak).
  public requestReResearch(id: number): Observable<ResearchJob> {
    return this.http.post<ResearchJob>(
      this.base(`/admin/editorial/${id}/re-research`),
      {}
    );
  }

  // ---- Job control ----------------------------------------------------------

  // Manual seeding, e.g. "Research JK1 now".
  public seedResearch(req: SeedResearchRequest): Observable<ResearchJob> {
    return this.http.post<ResearchJob>(
      this.base(`/admin/editorial/research`),
      req
    );
  }

  // Batch mode, e.g. "research next 100 most valuable recent sold plates".
  public batchResearch(
    req: BatchResearchRequest
  ): Observable<{ enqueued: number; jobs: ResearchJob[] }> {
    return this.http.post<{ enqueued: number; jobs: ResearchJob[] }>(
      this.base(`/admin/editorial/research/batch`),
      req
    );
  }

  public listJobs(status?: string): Observable<ResearchJob[]> {
    const q = status ? `?status=${status}` : '';
    return this.http.get<ResearchJob[]>(this.base(`/admin/editorial/jobs${q}`));
  }
}
